/**
 * Click a column heading to sort the table by it — A to Z, click again for Z
 * to A — on every data table in the app (`table.table`), without each page
 * wiring it up.
 *
 * Installed once by the Shell as a single delegated click handler. It sorts
 * the rows already on the page, comparing what each cell shows: numbers,
 * money, percentages and hours as numbers, dates as dates, everything else
 * as text (natural order, ignoring case). Blank cells and "—" always go last.
 *
 * WHAT IT LEAVES ALONE:
 * - a heading that sorts itself already (it carries aria-sort, like CSAT's)
 *   or holds a control (a button, a select, a checkbox);
 * - a table marked `data-nosort`;
 * - a table body with section rows in the middle (a row that does not span
 *   the full set of columns) — reordering those would scramble the sections.
 *   Total rows at the bottom stay at the bottom.
 *
 * The rows are React's. When React re-renders them — new data, a filter —
 * the sort is applied again, so the order sticks until another heading is
 * clicked.
 */

type Dir = 'asc' | 'desc';
interface State {
  col: number;
  dir: Dir;
}

const state = new WeakMap<HTMLTableElement, State>();
const observers = new WeakMap<HTMLTableSectionElement, MutationObserver>();

const BLANK = /^(—|–|-|n\/a)?$/i;
const NUMBER = /^[-+]?\(?[$£€]?\s?[-+]?[\d,]*\.?\d+\)?\s*(%|h|hrs?|hours?|x|k)?$/i;
/* A real month name is required: Date.parse alone accepts "gamma 2". */
const DATEISH =
  /^(\d{4}-\d{2}(-\d{2})?|(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.? \d{1,4}(,? \d{4})?|\d{1,2} (jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?( \d{4})?|\d{1,2}\/\d{1,2}\/\d{2,4})\b/i;

/** The value a cell sorts by: a number, a date, or text. */
function keyOf(cell: HTMLTableCellElement | undefined): { kind: 0 | 1 | 2 | 3; n: number; s: string } {
  const control = cell?.querySelector<HTMLInputElement | HTMLSelectElement>('input:not([type=checkbox]), select');
  const raw = (control ? control.value : (cell?.textContent ?? '')).replace(/\s+/g, ' ').trim();
  if (BLANK.test(raw)) return { kind: 3, n: 0, s: '' };
  const first = raw.split(' · ')[0]!.trim();
  if (NUMBER.test(first)) {
    const neg = /^\(.*\)$/.test(first) || /^-/.test(first.replace(/^[($£€\s]+/, ''));
    const n = Number(first.replace(/[^\d.]/g, ''));
    const k = /k$/i.test(first) ? 1000 : 1;
    if (Number.isFinite(n)) return { kind: 0, n: (neg ? -n : n) * k, s: raw };
  }
  if (DATEISH.test(first)) {
    const t = Date.parse(first);
    if (Number.isFinite(t)) return { kind: 1, n: t, s: raw };
  }
  return { kind: 2, n: 0, s: raw };
}

function compare(a: ReturnType<typeof keyOf>, b: ReturnType<typeof keyOf>, dir: Dir): number {
  /* Blanks last whichever way round. */
  if (a.kind === 3 || b.kind === 3) return a.kind === b.kind ? 0 : a.kind === 3 ? 1 : -1;
  let c: number;
  if (a.kind !== b.kind) c = a.kind - b.kind;
  else if (a.kind === 2) c = a.s.localeCompare(b.s, undefined, { numeric: true, sensitivity: 'base' });
  else c = a.n - b.n;
  return dir === 'asc' ? c : -c;
}

/** How many columns a row spans. */
const span = (row: HTMLTableRowElement) => [...row.cells].reduce((n, c) => n + (c.colSpan || 1), 0);

/** The cell in a row that sits under column `col`, allowing for colSpan. */
function cellAt(row: HTMLTableRowElement, col: number): HTMLTableCellElement | undefined {
  let i = 0;
  for (const c of row.cells) {
    const w = c.colSpan || 1;
    if (col < i + w) return c;
    i += w;
  }
  return undefined;
}

function sortBody(body: HTMLTableSectionElement, width: number, cells: number, s: State) {
  const rows = [...body.rows];
  /* A data row has a cell for every heading; a full-width section row (one
     cell spanning them all) and a total row are not data. */
  const full = rows.map((r) => span(r) === width && r.cells.length === cells && !/total/.test(r.className));
  /* Section rows anywhere but the end: leave this body alone. */
  const lastFull = full.lastIndexOf(true);
  if (full.slice(0, lastFull + 1).some((f) => !f)) return;
  const data = rows.filter((_, i) => full[i]);
  const tail = rows.filter((_, i) => !full[i]);
  const keyed = data.map((r, i) => ({ r, i, k: keyOf(cellAt(r, s.col)) }));
  keyed.sort((a, b) => compare(a.k, b.k, s.dir) || a.i - b.i);
  const order = [...keyed.map((x) => x.r), ...tail];
  if (order.every((r, i) => body.rows[i] === r)) return;
  const obs = observers.get(body);
  obs?.disconnect();
  for (const r of order) body.appendChild(r);
  obs?.observe(body, { childList: true });
}

function apply(table: HTMLTableElement) {
  const s = state.get(table);
  const head = table.tHead?.rows[table.tHead.rows.length - 1];
  if (!s || !head) return;
  const width = span(head);
  const cells = head.cells.length;
  for (const body of table.tBodies) {
    sortBody(body, width, cells, s);
    if (!observers.has(body)) {
      /* Re-sort after React changes the rows. */
      const obs = new MutationObserver(() => {
        const cur = state.get(table);
        if (cur) sortBody(body, width, cells, cur);
      });
      obs.observe(body, { childList: true });
      observers.set(body, obs);
    }
  }
  /* The arrow on the heading. */
  let i = 0;
  for (const th of head.cells) {
    const w = th.colSpan || 1;
    const on = s.col >= i && s.col < i + w;
    if (th.dataset.tsort !== undefined || on) {
      th.dataset.tsort = on ? s.dir : '';
      th.setAttribute('aria-sort', on ? (s.dir === 'asc' ? 'ascending' : 'descending') : 'none');
    }
    i += w;
  }
}

/** Whether a heading can be clicked to sort. */
export function sortableHeading(th: HTMLTableCellElement): HTMLTableElement | null {
  const table = th.closest('table');
  if (!table || !table.classList.contains('table') || table.hasAttribute('data-nosort')) return null;
  if (!th.closest('thead') || th.closest('table') !== table) return null;
  if (th.hasAttribute('aria-sort') && th.dataset.tsort === undefined) return null; // sorts itself
  if (th.querySelector('button, select, input, a')) return null;
  if (!th.textContent?.trim()) return null;
  return table;
}

export function installTableSorting(): () => void {
  const onClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target || target.closest('button, a, input, select, label, textarea')) return;
    const th = target.closest('th');
    if (!th) return;
    const table = sortableHeading(th as HTMLTableCellElement);
    if (!table) return;
    const row = th.parentElement as HTMLTableRowElement;
    let col = 0;
    for (const c of row.cells) {
      if (c === th) break;
      col += c.colSpan || 1;
    }
    const cur = state.get(table);
    const dir: Dir = cur && cur.col === col && cur.dir === 'asc' ? 'desc' : 'asc';
    state.set(table, { col, dir });
    apply(table);
  };
  /* Marks headings that can be sorted, for the pointer and the hint. */
  const onOver = (e: MouseEvent) => {
    const th = (e.target as HTMLElement | null)?.closest?.('th');
    if (th && th.dataset.tsortable === undefined) {
      th.dataset.tsortable = sortableHeading(th as HTMLTableCellElement) ? '1' : '0';
      if (th.dataset.tsortable === '1' && !th.title) th.title = 'Sort A–Z / Z–A';
    }
  };
  document.addEventListener('click', onClick);
  document.addEventListener('mouseover', onOver);
  return () => {
    document.removeEventListener('click', onClick);
    document.removeEventListener('mouseover', onOver);
  };
}
